<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { watchDebounced } from "@vueuse/core";
  import { Check, Pencil, Plus, Search } from "lucide-vue-next";
  import { FormDialog } from "@/components/episto-ui/form-dialog";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { useGridsStore } from "@stores/survey/grids/grids";

  const open = defineModel<boolean>("open", { required: true });
  const emit = defineEmits<{ added: []; "edit-population": [token: string | null] }>();

  const gridsStore = useGridsStore();
  const { currentGrid, candidates, populations } = storeToRefs(gridsStore);
  const { addRows, addPopulations, searchCandidates, fetchPopulations } = gridsStore;

  const isPopulationGrid = computed(() => currentGrid.value?.grid_type === "population");

  const search = ref("");
  const selectedIds = ref<number[]>([]);
  const selectedTokens = ref<string[]>([]);
  const submitting = ref(false);

  const alreadyInGrid = computed(() => (currentGrid.value?.rows ?? []).map((row) => row.survey_customer_id));
  const available = computed(() => candidates.value.filter((interview) => !alreadyInGrid.value.includes(interview.id)));

  const availablePopulations = computed(() => {
    const taken = (currentGrid.value?.rows ?? []).map((row) => row.search_group_id);
    return populations.value.filter((population) => !taken.includes(population.id));
  });

  const toggle = (id: number) => {
    selectedIds.value = selectedIds.value.includes(id)
      ? selectedIds.value.filter((selected) => selected !== id)
      : [...selectedIds.value, id];
  };

  const toggleToken = (token: string) => {
    selectedTokens.value = selectedTokens.value.includes(token)
      ? selectedTokens.value.filter((selected) => selected !== token)
      : [...selectedTokens.value, token];
  };

  const submit = async () => {
    submitting.value = true;
    if (isPopulationGrid.value) await addPopulations(selectedTokens.value);
    else await addRows(selectedIds.value, selectedTokens.value);
    submitting.value = false;
    emit("added");
    open.value = false;
  };

  watch(open, async (isOpen) => {
    if (!isOpen) return;

    search.value = "";
    selectedIds.value = [];
    selectedTokens.value = [];
    await fetchPopulations();
    if (!isPopulationGrid.value) await searchCandidates("");
  });

  watchDebounced(
    search,
    () => {
      if (!isPopulationGrid.value) searchCandidates(search.value);
    },
    { debounce: 300 },
  );
</script>

<template>
  <FormDialog
    :open="open"
    :title="isPopulationGrid ? 'Add populations' : 'Add respondents'"
    confirm-label="Add"
    max-width="480px"
    :loading="submitting"
    :can-save="selectedIds.length > 0 || selectedTokens.length > 0"
    @update:open="open = $event"
    @save="submit">
    <div
      v-if="isPopulationGrid"
      class="space-y-1">
      <button
        v-for="population in availablePopulations"
        :key="population.token"
        type="button"
        class="flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-13 transition-colors hover:bg-muted"
        :class="{ 'border-primary bg-primary/5': selectedTokens.includes(population.token) }"
        @click="toggleToken(population.token)">
        <span class="flex min-w-0 items-center gap-2">
          <span
            class="size-2 shrink-0 rounded-full"
            :style="{ backgroundColor: population.color || 'transparent' }" />
          <span class="truncate">{{ population.name }}</span>
        </span>
        <span class="flex shrink-0 items-center gap-2">
          <Pencil
            class="size-3.5 text-muted-foreground hover:text-primary"
            @click.stop="emit('edit-population', population.token)" />
          <Check
            v-if="selectedTokens.includes(population.token)"
            class="size-4 text-primary" />
        </span>
      </button>
      <p
        v-if="!availablePopulations.length"
        class="py-4 text-center text-13 text-muted-foreground">
        All populations are already in this grid.
      </p>
      <Button
        variant="ghost"
        size="sm"
        @click="emit('edit-population', null)">
        <Plus class="size-3.5" />
        New population
      </Button>
    </div>

    <div
      v-else
      class="flex min-h-0 flex-col gap-2">
      <Input
        v-model="search"
        placeholder="Search respondents..."
        :icon="Search"
        clearable />

      <div
        v-if="populations.length"
        class="flex flex-wrap items-center gap-1.5">
        <span class="text-xs text-muted-foreground">Add everyone from:</span>
        <button
          v-for="population in populations"
          :key="population.token"
          type="button"
          class="flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted"
          :class="{ 'border-primary bg-primary/5 text-foreground': selectedTokens.includes(population.token) }"
          @click="toggleToken(population.token)">
          <span
            class="size-2 shrink-0 rounded-full"
            :style="{ backgroundColor: population.color || 'transparent' }" />
          {{ population.name }}
        </button>
      </div>

      <div class="max-h-[50vh] space-y-1 overflow-y-auto pr-1">
        <button
          v-for="interview in available"
          :key="interview.id"
          type="button"
          class="flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-13 transition-colors hover:bg-muted"
          :class="{ 'border-primary bg-primary/5': selectedIds.includes(interview.id) }"
          @click="toggle(interview.id)">
          <span class="flex min-w-0 items-center gap-2">
            <span class="shrink-0 font-medium text-primary">#{{ interview.token }}</span>
            <span class="truncate text-xs text-muted-foreground">{{ interview.quick_take }}</span>
          </span>
          <Check
            v-if="selectedIds.includes(interview.id)"
            class="size-4 shrink-0 text-primary" />
        </button>
        <p
          v-if="!available.length"
          class="py-4 text-center text-13 text-muted-foreground">
          Every respondent is already in this grid.
        </p>
      </div>
    </div>
  </FormDialog>
</template>
